// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DEFAULT_UNIT_OF_MEASURE } from '@/constants/unit-of-measure';
import { Item } from '@/items/api/types';
import { SparseItem } from '@/sparse/api/types';

/**
 * Represents a parsed row from a shipment items CSV file.
 */
interface ParsedCSVRow {
  sku: string;
  quantity: number;
}

/**
 * Represents a form item for shipment items using the unified item structure.
 */
interface ShipmentFormItem {
  item: SparseItem;
  quantity: number;
}

/**
 * Result of parsing and looking up CSV items.
 */
interface ParseCSVResult {
  formItems: ShipmentFormItem[];
  notFoundSkus: string[];
}

/**
 * Parses a CSV string and returns an array of objects with sku and quantity.
 * Expects a header row with "sku" and "quantity" columns (case-insensitive).
 *
 * @param text - The CSV text content
 * @returns Array of parsed rows with sku and quantity
 * @throws Error if CSV is missing required columns or has invalid data
 */
export function parseShipmentItemsCSV(text: string): ParsedCSVRow[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length < 2) {
    return [];
  }

  // Parse header row to find column indices
  const headerLine = lines[0];
  const headers = headerLine.split(',').map((h) => h.trim().toLowerCase());
  const skuIndex = headers.findIndex((h) => h === 'sku');
  const quantityIndex = headers.findIndex(
    (h) => h === 'quantity' || h === 'qty'
  );

  if (skuIndex === -1 || quantityIndex === -1) {
    throw new Error('CSV must have "sku" and "quantity" (or "qty") columns');
  }

  // Parse data rows
  const results: ParsedCSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = line.split(',').map((v) => v.trim());
    const sku = values[skuIndex];
    const quantityStr = values[quantityIndex];

    if (!sku) continue; // Skip empty rows

    const quantity = parseInt(quantityStr, 10);
    if (isNaN(quantity) || quantity <= 0) {
      throw new Error(
        `Invalid quantity on row ${String(i + 1)}: "${quantityStr}"`
      );
    }

    results.push({ sku, quantity });
  }

  return results;
}

/**
 * Looks up parsed CSV rows against unified items by SKU.
 * Returns form items for matched SKUs and a list of SKUs that weren't found.
 *
 * @param parsedItems - Array of parsed CSV rows with sku and quantity
 * @param items - Array of all items to search
 * @returns Object containing matched form items and not found SKUs
 */
export function lookupShipmentItemsBySkuFromItems(
  parsedItems: ParsedCSVRow[],
  items: Item[]
): ParseCSVResult {
  // Build SKU lookup map for items
  const itemsBySku = new Map(
    items.map((item) => [item.sku.toLowerCase(), item])
  );

  // Convert parsed items to form items
  const formItems: ShipmentFormItem[] = [];
  const notFoundSkus: string[] = [];

  for (const { sku, quantity } of parsedItems) {
    const lowerSku = sku.toLowerCase();

    const item = itemsBySku.get(lowerSku);
    if (item) {
      // Convert Item to SparseItem for form
      const isDevice = item.type === 'DEVICE';
      formItems.push({
        item: {
          id: item.id,
          key: item.key,
          name: item.name,
          type: item.type,
          sku: item.sku,
          isArchived: item.isArchived,
          unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
          hasAssets: isDevice,
          isPhysical: item.type !== 'SERVICE',
          shouldRenderSerialFields: item.shouldRenderSerialFields,
        },
        quantity,
      });
      continue;
    }

    // SKU not found
    notFoundSkus.push(sku);
  }

  return { formItems, notFoundSkus };
}
